import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'geely_selected_store';

interface StoreContextType {
  selectedStoreId: string | null;
  setSelectedStoreId: (id: string) => void;
  userStoreIds: string[];
  isMultiStore: boolean;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const userStoreIds = useMemo(() => user?.storeIds ?? [], [user?.storeIds]);
  const isMultiStore = userStoreIds.length > 1;

  const [selectedStoreId, setSelectedStoreIdState] = useState<string | null>(() => {
    if (userStoreIds.length === 0) return null;

    const persisted = localStorage.getItem(STORAGE_KEY);
    if (persisted && userStoreIds.includes(persisted)) {
      return persisted;
    }

    return userStoreIds[0];
  });

  // Sync when user changes (login/logout or storeIds update)
  useEffect(() => {
    if (userStoreIds.length === 0) {
      setSelectedStoreIdState(null);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // If current selection is still valid, keep it
    if (selectedStoreId && userStoreIds.includes(selectedStoreId)) {
      return;
    }

    // Fallback to persisted or first
    const persisted = localStorage.getItem(STORAGE_KEY);
    if (persisted && userStoreIds.includes(persisted)) {
      setSelectedStoreIdState(persisted);
    } else {
      setSelectedStoreIdState(userStoreIds[0]);
    }
  }, [userStoreIds, selectedStoreId]);

  const setSelectedStoreId = useCallback((id: string) => {
    setSelectedStoreIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  return (
    <StoreContext.Provider
      value={{
        selectedStoreId,
        setSelectedStoreId,
        userStoreIds,
        isMultiStore,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useSelectedStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useSelectedStore must be used within a StoreProvider');
  }
  return context;
}
